import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { adminApi, ApiError, type AdminUser } from './admin-api';

/**
 * The admin user-management console.
 *
 * Renders a searchable, paginated table of users with per-row role / enable-
 * disable / delete actions and a "Create user" modal. Every mutation goes
 * through {@link adminApi} (Bearer-authed, server-enforced) and refreshes the
 * current page on success. Server `{ error }` messages are surfaced verbatim
 * (banner for list/action failures, inline in the create modal).
 *
 * The signed-in admin's own email is passed in via {@link meEmail}; we disable
 * the footgun actions (disable / delete / demote) on that row even though the
 * server also guards them, so the UI never offers a self-destructive control.
 *
 * Light-DOM (no shadow root) so the page picks up admin.css + the Paper tokens
 * loaded by index.html, exactly like the decks shell's pages.
 */
@customElement('admin-users-page')
export class AdminUsersPage extends LitElement {
  /** The signed-in admin's email — used to disable self-targeting actions. */
  @property({ type: String }) meEmail = '';

  /** Current page of users. */
  @state() private users: AdminUser[] = [];
  /** Total matching the current query (drives the X–Y of total + Next). */
  @state() private total = 0;
  /** Debounced search term sent as the `query` param. */
  @state() private query = '';
  /** Zero-based row offset of the current page. */
  @state() private offset = 0;

  @state() private loading = true;
  /** List-level error (banner). */
  @state() private error: string | null = null;
  /** id of the row whose action is currently in flight (disables that row). */
  @state() private busyId: string | null = null;

  /** Create-user modal open + its in-flight + inline-error state. */
  @state() private createOpen = false;
  @state() private createBusy = false;
  @state() private createError: string | null = null;

  /** Confirm-delete dialog target (null = closed). */
  @state() private confirmDelete: AdminUser | null = null;
  @state() private deleteBusy = false;

  /** Page size — 50 per the brief. */
  private readonly limit = 50;
  /** Debounce handle for the search box. */
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  // ─── Data ──────────────────────────────────────────────────────────────
  private async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const res = await adminApi.listUsers(this.query, this.limit, this.offset);
      this.users = res.users ?? [];
      this.total = res.total ?? 0;
    } catch (err) {
      this.error = errMessage(err);
      this.users = [];
      this.total = 0;
    } finally {
      this.loading = false;
    }
  }

  // ─── Search (debounced) ──────────────────────────────────────────────────
  private onSearchInput = (e: Event): void => {
    const value = (e.target as HTMLInputElement).value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.query = value;
      this.offset = 0; // a new search resets to the first page
      void this.load();
    }, 300);
  };

  // ─── Pagination ──────────────────────────────────────────────────────────
  private get hasPrev(): boolean {
    return this.offset > 0;
  }
  private get hasNext(): boolean {
    return this.offset + this.users.length < this.total;
  }
  private prevPage = (): void => {
    if (!this.hasPrev || this.loading) return;
    this.offset = Math.max(0, this.offset - this.limit);
    void this.load();
  };
  private nextPage = (): void => {
    if (!this.hasNext || this.loading) return;
    this.offset += this.limit;
    void this.load();
  };

  // ─── Row actions ─────────────────────────────────────────────────────────
  // Each wraps one mutation, marks the row busy, surfaces any server {error}
  // in the banner, and reloads the current page on success.
  private async runRowAction(id: string, fn: () => Promise<unknown>): Promise<void> {
    if (this.busyId) return; // serialise: one row action at a time
    this.busyId = id;
    this.error = null;
    try {
      await fn();
      await this.load();
    } catch (err) {
      this.error = errMessage(err);
    } finally {
      this.busyId = null;
    }
  }

  private toggleRole = (u: AdminUser): void => {
    const next = u.role === 'admin' ? 'member' : 'admin';
    void this.runRowAction(u.id, () => adminApi.setRole(u.id, next));
  };
  private toggleBan = (u: AdminUser): void => {
    void this.runRowAction(u.id, () => (u.banned ? adminApi.unban(u.id) : adminApi.ban(u.id)));
  };

  // ─── Delete (confirm dialog) ─────────────────────────────────────────────
  private askDelete = (u: AdminUser): void => {
    this.confirmDelete = u;
  };
  private cancelDelete = (): void => {
    if (this.deleteBusy) return;
    this.confirmDelete = null;
  };
  private confirmDeleteNow = async (): Promise<void> => {
    const u = this.confirmDelete;
    if (!u || this.deleteBusy) return;
    this.deleteBusy = true;
    this.error = null;
    try {
      await adminApi.remove(u.id);
      this.confirmDelete = null;
      // If we just removed the last row of a non-first page, step back so the
      // user isn't stranded on an empty page.
      if (this.users.length === 1 && this.offset > 0) {
        this.offset = Math.max(0, this.offset - this.limit);
      }
      await this.load();
    } catch (err) {
      this.error = errMessage(err);
    } finally {
      this.deleteBusy = false;
    }
  };

  // ─── Create user ─────────────────────────────────────────────────────────
  private openCreate = (): void => {
    this.createError = null;
    this.createOpen = true;
  };
  private closeCreate = (): void => {
    if (this.createBusy) return;
    this.createOpen = false;
  };
  private onCreateSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    if (this.createBusy) return;
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const email = String(data.get('email') ?? '').trim();
    const name = String(data.get('name') ?? '').trim();
    const password = String(data.get('password') ?? '');
    const role = (String(data.get('role') ?? 'member') === 'admin' ? 'admin' : 'member') as
      | 'admin'
      | 'member';
    if (!email || !password) {
      this.createError = 'Email and password are required.';
      return;
    }
    this.createBusy = true;
    this.createError = null;
    try {
      await adminApi.createUser({ email, password, role, ...(name ? { name } : {}) });
      this.createOpen = false;
      this.offset = 0; // surface the new user from the top
      this.query = '';
      await this.load();
    } catch (err) {
      // 403 non-Ricoh · 400 bad/missing · 422 weak password → server message.
      this.createError = errMessage(err);
    } finally {
      this.createBusy = false;
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  render(): TemplateResult {
    return html`
      <div class="page admin-page">
        <div class="page-head">
          <div class="titles">
            <h1>Users</h1>
            <p class="page-sub">Manage who can access Workplace by Ricoh.</p>
          </div>
          <button class="btn-primary" type="button" @click=${this.openCreate}>
            Create user
          </button>
        </div>

        <div class="admin-toolbar">
          <input
            class="input admin-search"
            type="search"
            placeholder="Search by name or email…"
            aria-label="Search users"
            ?disabled=${this.loading && this.users.length === 0}
            @input=${this.onSearchInput}
          />
        </div>

        ${this.error ? html`<p class="admin-banner" role="alert">${this.error}</p>` : nothing}
        ${this.renderBody()} ${this.renderPager()} ${this.renderCreateModal()}
        ${this.renderDeleteDialog()}
      </div>
    `;
  }

  private renderBody(): TemplateResult {
    if (this.loading && this.users.length === 0) {
      return html`<div class="state" role="status">Loading users…</div>`;
    }
    if (!this.loading && this.users.length === 0) {
      return html`<div class="state">
        ${this.query
          ? html`No users match “${this.query}”.`
          : html`No users yet. Create the first one.`}
      </div>`;
    }
    return html`
      <div class="admin-table-wrap" aria-busy=${this.loading ? 'true' : 'false'}>
        <table class="admin-table">
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Last sign-in</th>
              <th scope="col">Created</th>
              <th scope="col" class="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.users.map((u) => this.renderRow(u))}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderRow(u: AdminUser): TemplateResult {
    const isSelf = !!this.meEmail && u.email.toLowerCase() === this.meEmail.toLowerCase();
    const rowBusy = this.busyId === u.id;
    const disabled = rowBusy || !!this.busyId;
    return html`
      <tr class=${u.banned ? 'is-disabled' : ''}>
        <td>
          <div class="user-cell">
            ${u.imageUrl
              ? html`<img class="avatar" src=${u.imageUrl} alt="" loading="lazy" />`
              : html`<span class="avatar avatar-fallback" aria-hidden="true"
                  >${initials(u.name || u.email)}</span
                >`}
            <div class="user-meta">
              <span class="user-name"
                >${u.name || '—'}${isSelf ? html`<span class="you-pill">You</span>` : nothing}</span
              >
              <span class="user-email">${u.email}</span>
            </div>
          </div>
        </td>
        <td>
          <span class="badge ${u.role === 'admin' ? 'badge-admin' : 'badge-member'}"
            >${u.role}</span
          >
        </td>
        <td>${this.renderStatus(u)}</td>
        <td class="num">${u.lastSignInAt ? relTime(u.lastSignInAt) : html`<span class="muted">Never</span>`}</td>
        <td class="num">${u.createdAt ? dateOnly(u.createdAt) : html`<span class="muted">—</span>`}</td>
        <td class="col-actions">
          <div class="row-actions">
            <button
              class="cell-action"
              type="button"
              ?disabled=${disabled || isSelf}
              title=${isSelf ? 'You cannot change your own role' : ''}
              @click=${() => this.toggleRole(u)}
            >
              ${u.role === 'admin' ? 'Make member' : 'Make admin'}
            </button>
            <button
              class="cell-action"
              type="button"
              ?disabled=${disabled || isSelf}
              title=${isSelf ? 'You cannot disable your own account' : ''}
              @click=${() => this.toggleBan(u)}
            >
              ${u.banned ? 'Enable' : 'Disable'}
            </button>
            <button
              class="cell-action danger"
              type="button"
              ?disabled=${disabled || isSelf}
              title=${isSelf ? 'You cannot delete your own account' : ''}
              @click=${() => this.askDelete(u)}
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
    `;
  }

  private renderStatus(u: AdminUser): TemplateResult {
    if (u.banned) return html`<span class="status status-disabled">Disabled</span>`;
    if (u.locked) return html`<span class="status status-locked">Locked</span>`;
    return html`<span class="status status-active">Active</span>`;
  }

  private renderPager(): TemplateResult | typeof nothing {
    if (this.users.length === 0) return nothing;
    const from = this.offset + 1;
    const to = this.offset + this.users.length;
    return html`
      <div class="admin-pager">
        <span class="pager-count">${from}–${to} of ${this.total}</span>
        <div class="pager-buttons">
          <button
            class="btn-secondary"
            type="button"
            ?disabled=${!this.hasPrev || this.loading}
            @click=${this.prevPage}
          >
            Previous
          </button>
          <button
            class="btn-secondary"
            type="button"
            ?disabled=${!this.hasNext || this.loading}
            @click=${this.nextPage}
          >
            Next
          </button>
        </div>
      </div>
    `;
  }

  private renderCreateModal(): TemplateResult | typeof nothing {
    if (!this.createOpen) return nothing;
    return html`
      <div
        class="admin-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.closeCreate();
        }}
      >
        <div class="admin-modal" role="dialog" aria-modal="true" aria-labelledby="create-title">
          <div class="modal-head">
            <h2 id="create-title">Create user</h2>
            <button
              class="icon-btn"
              type="button"
              aria-label="Close"
              ?disabled=${this.createBusy}
              @click=${this.closeCreate}
            >
              ✕
            </button>
          </div>
          <form @submit=${this.onCreateSubmit} novalidate>
            <div class="field">
              <label for="cu-email">Email</label>
              <input
                id="cu-email"
                class="input"
                name="email"
                type="email"
                autocomplete="off"
                placeholder="person@ricoh-europe.com"
                ?disabled=${this.createBusy}
                required
              />
            </div>
            <div class="field">
              <label for="cu-name">Name <span class="optional">(optional)</span></label>
              <input
                id="cu-name"
                class="input"
                name="name"
                type="text"
                autocomplete="off"
                placeholder="Full name"
                ?disabled=${this.createBusy}
              />
            </div>
            <div class="field">
              <label for="cu-role">Role</label>
              <select id="cu-role" class="select" name="role" ?disabled=${this.createBusy}>
                <option value="member" selected>Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div class="field">
              <label for="cu-password">Password</label>
              <input
                id="cu-password"
                class="input"
                name="password"
                type="password"
                autocomplete="new-password"
                placeholder="Set a temporary password"
                ?disabled=${this.createBusy}
                required
              />
            </div>
            ${this.createError
              ? html`<p class="err" role="alert">${this.createError}</p>`
              : nothing}
            <div class="modal-actions">
              <button
                class="btn-secondary"
                type="button"
                ?disabled=${this.createBusy}
                @click=${this.closeCreate}
              >
                Cancel
              </button>
              <button class="btn-primary" type="submit" ?disabled=${this.createBusy}>
                ${this.createBusy ? 'Creating…' : 'Create user'}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private renderDeleteDialog(): TemplateResult | typeof nothing {
    const u = this.confirmDelete;
    if (!u) return nothing;
    return html`
      <div
        class="admin-overlay"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.cancelDelete();
        }}
      >
        <div class="admin-modal small" role="dialog" aria-modal="true" aria-labelledby="del-title">
          <div class="modal-head">
            <h2 id="del-title">Delete user</h2>
          </div>
          <p class="modal-text">
            Permanently delete <strong>${u.name || u.email}</strong>? This cannot be undone.
          </p>
          ${this.error ? html`<p class="err" role="alert">${this.error}</p>` : nothing}
          <div class="modal-actions">
            <button
              class="btn-secondary"
              type="button"
              ?disabled=${this.deleteBusy}
              @click=${this.cancelDelete}
            >
              Cancel
            </button>
            <button
              class="btn-primary danger-btn"
              type="button"
              ?disabled=${this.deleteBusy}
              @click=${this.confirmDeleteNow}
            >
              ${this.deleteBusy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

// ─── helpers ───────────────────────────────────────────────────────────────

/** Pull a user-facing message off an unknown thrown value. */
function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

/** Up-to-two-letter initials for the avatar fallback. */
function initials(s: string): string {
  const parts = s.trim().split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Date-only formatting (e.g. "2 Jun 2026") for the Created column. */
function dateOnly(ms: number): string {
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/** Humanised relative time (e.g. "3 days ago") for last sign-in. Falls back to
 *  an absolute date for anything older than ~30 days. */
function relTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return dateOnly(ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  return dateOnly(ms);
}

declare global {
  interface HTMLElementTagNameMap {
    'admin-users-page': AdminUsersPage;
  }
}
